import { MeetingChatMessage } from '../../../../interfaces/interfaces';
import { Message, MessageStyle } from './Message';
import { InputStyle, MessageInput } from './MessageInput';
import { ControlPanel, ControlPanelStyle } from './ControlPanel';
import { ReplyTo } from './ReplyTo';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getScrollPositionFromBottom, scrollToBottomOfELementById } from '../../../../utils/functions';
import { useTranslation } from 'next-i18next';
import { NewMessageNotification } from './NewMessageNotification';
import { useChat } from '../../../meeting/hooks/useChat';

interface MessageContextProps {
  showHighlights?: boolean;
  hasNewMessage?: boolean;
  messages?: MeetingChatMessage[];
  replyTo?: MeetingChatMessage;
  setShowHighlights: (show: boolean) => void;
  setHasNewMessage: (hasNewMessage: boolean) => void;
  setReplyTo: (message?: MeetingChatMessage) => void;
  fetchMessages: () => void;
}

export const MessageContext = createContext<MessageContextProps>({
  setShowHighlights: () => {},
  setHasNewMessage: () => {},
  setReplyTo: () => {},
  fetchMessages: () => {},
});

export interface BaseChatProps {
  messages?: MeetingChatMessage[];
  pusherChannelName?: string;
  chatExportId?: string;
  messageTimeout?: number;
  isStage?: boolean;
  canSend?: boolean;
  isAdmin?: boolean;
  isChatLocked?: boolean;
  showClearChat?: boolean;
  showExport?: boolean;
  showHighlightsControl?: boolean;
  showExternalLink?: boolean;
  showChatLock?: boolean;
  isMessagingCenter?: boolean;
  inputStyle?: InputStyle;
  messageStyle?: MessageStyle;
  controlPanelStyle?: ControlPanelStyle;
  onSendMessage?: (message: string, replyTo?: MeetingChatMessage) => void;
  fetchMessages?: () => void;
}

export const BaseChat = ({
  messages,
  pusherChannelName,
  chatExportId,
  messageTimeout,
  isStage,
  canSend,
  isAdmin,
  isChatLocked,
  showClearChat,
  showExport,
  showHighlightsControl,
  showExternalLink,
  showChatLock,
  isMessagingCenter = false,
  inputStyle,
  messageStyle,
  controlPanelStyle,
  onSendMessage,
  fetchMessages,
}: BaseChatProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [userColors, setUserColors] = useState<{ [key: string]: string }>({});
  const [showHighlights, setShowHighlights] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [replyTo, setReplyTo] = useState<MeetingChatMessage | undefined>();

  const controlPanelRef = useRef<HTMLDivElement>(null);

  const { checkIfUserIsBannedFromChat } = useChat();
  const { t } = useTranslation();

  function init() {
    if (isInitialized) return;

    setTimeout(() => scrollToBottomOfELementById('messages'), 200);

    setIsInitialized(true);
  }

  function handleScroll() {
    const scrollPosition = getScrollPositionFromBottom('messages');
    if (scrollPosition < 150) {
      scrollToBottomOfELementById('messages');
    } else if (scrollPosition > 150) {
      setHasNewMessage(true);
    }
  }

  function handleAvatarColors() {
    if (!messages || messages?.length === 0) return;

    const userColorsCopy = {
      ...userColors,
    };

    messages.forEach((message) => {
      if (!userColorsCopy[message.user._id]) {
        const randomColor = Math.floor(Math.random() * 16777215).toString(16);
        userColorsCopy[message.user._id] = `#${randomColor}`;
      }
    });

    setUserColors(userColorsCopy);
  }

  function getMessages() {
    if (!showHighlights) return messages!;

    return messages!.filter((i) => i.isHighlighted);
  }

  function calcChatBottomMargin() {
    return (controlPanelRef.current?.clientHeight! > 10 ? 70 : 0) + (onSendMessage ? 50 : 0);
  }

  useEffect(() => {
    checkIfUserIsBannedFromChat();
  }, []);

  useEffect(() => {
    if (!messages || messages?.length === 0) return;
    init();
    handleScroll();
    handleAvatarColors();
  }, [messages]);

  return (
    <MessageContext.Provider
      value={{ messages, replyTo, showHighlights, hasNewMessage, setShowHighlights, setHasNewMessage, setReplyTo, fetchMessages: () => fetchMessages?.() }}>
      <div className="flex relative flex-col justify-between w-full h-full">
        {isChatLocked ? (
          <p className={controlPanelStyle?.headerClassName}>{t('chat:locked')}</p>
        ) : (
          <div
            id="messages"
            className={`flex ${isStage ? 'h-96 min-[818px]:h-full' : 'max-h-full h-full'} relative flex-col gap-4 overflow-y-scroll`}
            style={{ marginBottom: calcChatBottomMargin() }}>
            {getMessages().map((message) => (
              <Message
                key={message._id}
                message={message}
                style={{ ...messageStyle, avatar: { background: userColors[message.user._id] } }}
                enableHighlights={!isStage}
              />
            ))}
            <NewMessageNotification />
          </div>
        )}
        {onSendMessage && (
          <div className="absolute bottom-0 px-2 w-full backdrop-blur">
            {!isChatLocked && <ReplyTo />}
            {!isChatLocked && <MessageInput onSendMessage={onSendMessage} canSend={canSend} messageTimeout={messageTimeout} style={inputStyle} />}
            <div ref={controlPanelRef}>
              <ControlPanel
                showClearChat={showClearChat}
                showExport={showExport}
                showHighlightsControl={showHighlightsControl}
                chatExportId={chatExportId}
                showChatLock={showChatLock}
                showExternalLink={showExternalLink}
                isStage={isStage}
                isChatLocked={isChatLocked}
                style={controlPanelStyle}
              />
            </div>
          </div>
        )}
      </div>
    </MessageContext.Provider>
  );
};
